import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ValidationErrorLabel} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ParentStorableValidationErrorsQuery,
  ParentStorableValidationErrorsQueryVariables,
} from 'inventory/location-validation/__generated/ParentStorableValidationErrorsQuery';
import navigateToURL from 'lib/navigateToURL';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {Filter, FilterKey, Operator, SortKey} from 'search/SearchConstants';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {listToReadable} from 'util/NameUtil';

export const PARENT_STORABLE_VALIDATION_ERRORS_QUERY = gql`
  query ParentStorableValidationErrorsQuery($storableId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storable(id: $storableId) {
      ... on LocationNode {
        id
        capacity
        occupiedCapacity
      }
      ... on StorableInterface {
        id
        validationState {
          id
          status
          validationErrorRecords {
            id
            errorLabel
            message
            relatedItems {
              ... on StorableInterface {
                id
                barcode {
                  value
                }
                storableSchema {
                  id
                  registry {
                    id
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ParentStorableValidationErrorProps {
  parentStorableId: string;
  registryId: string;
}

type ParentWithValidationState = ExcludeInaccessible<ParentStorableValidationErrorsQuery['storable']>;
export type ParentValidationState = ExtractByTypename<
  ParentWithValidationState['validationState'],
  'ValidationStateNode'
>;
export type ValidationErrorRecordGQL = NonNullable<ParentValidationState['validationErrorRecords']>[number];
type ValidationErrorRelatedItem = ExtractByTypename<
  ValidationErrorRecordGQL['relatedItems'][number],
  'BoxNode'
>;
export interface StorableValidationErrorRecord {
  message: string;
  errorLabel: ValidationErrorLabel;
  relatedItemBarcodes: Array<string>;
  /** This number may be greater than the length of relatedItemBarcodes; it intentionally includes all
   * inaccessible or otherwise invalid items as well. */
  totalNumberOfRelatedItems: number;
  /** Possible subset of relatedItemBarcodes that will not be surfaceable in search due to belonging to a
   * different registry. */
  incorrectRegistryRelatedItemBarcodes?: Array<string>;
}

interface QueryingProps {
  parentStorable: ParentWithValidationState;
}

export function getValidationSearchUrl(
  itemBarcodes: Array<string>,
  registryId: string,
  parentStorableId?: string
): string {
  const filters: Array<Filter> = [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [
        ModelTypeConstants.LOCATION,
        ModelTypeConstants.PLATE,
        ModelTypeConstants.BOX,
        ModelTypeConstants.CONTAINER,
      ],
    },
    {
      key: FilterKey.REGISTRY,
      operator: Operator.IS,
      value: registryId,
    },
    ...(parentStorableId != null
      ? [
          {
            key: FilterKey.PARENT_STORABLE as const,
            operator: Operator.IS,
            value: parentStorableId,
          },
        ]
      : []),
    ...(itemBarcodes.length > 0
      ? [
          {
            key: FilterKey.BARCODES as const,
            operator: Operator.IS_ONE_OF,
            value: itemBarcodes,
          },
        ]
      : []),
  ];
  return getNavigationURL(
    '/storage',
    {
      offset: 0,
      limit: 25,
      sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
      filters,
      query: '',
    },
    filters.map((_) => null)
  );
}

export function parseStorableValidationErrorRelatedItems(
  validationErrorRecords: Array<ValidationErrorRecordGQL>,
  targetRegistryId?: string
): Array<StorableValidationErrorRecord> {
  // Filter down related items to only valid storables. Others (inaccessible items, or entities which should
  // not be possible) cannot be included in the search link that this property populates, and should not be
  // shown to the user.
  const getStorableRelatedItems = (error: ValidationErrorRecordGQL): Array<ValidationErrorRelatedItem> =>
    (error.relatedItems ?? []).flatMap((item) =>
      ['ContainerNode', 'BoxNode', 'PlateNode', 'LocationNode'].includes(item.__typename) &&
      (item as ValidationErrorRelatedItem).barcode?.value != null
        ? [item as ValidationErrorRelatedItem]
        : []
    );
  return validationErrorRecords.map((error) => {
    const relatedItems = getStorableRelatedItems(error);
    return {
      message: error.message,
      errorLabel: error.errorLabel,
      relatedItemBarcodes: relatedItems.map((item) => item.barcode!.value),
      ...(targetRegistryId != null && {
        incorrectRegistryRelatedItemBarcodes: relatedItems.flatMap((item) =>
          item.storableSchema.registry.id !== targetRegistryId ? [item.barcode!.value] : []
        ),
      }),
      totalNumberOfRelatedItems: (error.relatedItems ?? []).length,
    };
  });
}

function ParentStorableValidationErrors({
  registryId,
  parentStorable,
}: QueryingProps & ParentStorableValidationErrorProps): JSX.Element | null {
  const validationErrors = parentStorable.validationState?.validationErrorRecords ?? null;
  if (!isFailed(parentStorable.validationState) || !validationErrors) {
    return null;
  }

  const errorsWithRelatedBarcodes = parseStorableValidationErrorRelatedItems(validationErrors, registryId);
  const occupiedSpaceString =
    parentStorable.__typename === 'LocationNode' &&
    parentStorable.capacity != null &&
    parentStorable.occupiedCapacity != null
      ? ` Occupied space: ${parentStorable.occupiedCapacity} / ${parentStorable.capacity}`
      : '';

  const errorMessages: Array<InlineBannerProps> = errorsWithRelatedBarcodes.map((error) => {
    if (error.errorLabel === ValidationErrorLabel.OVER_CAPACITY) {
      return {
        bannerType: InlineBannerType.ERROR,
        text: `${error.message}${occupiedSpaceString}`,
        action: {
          actionType: InlineBannerActionType.CUSTOM,
          actionText: 'Show all items in search',
          actionOnClick: () => navigateToURL(getValidationSearchUrl([], registryId, parentStorable.id)),
        },
      };
    }

    const relatedItemBarcodesOfCorrectRegistry = error.relatedItemBarcodes.filter(
      (barcode) => !(error.incorrectRegistryRelatedItemBarcodes ?? []).includes(barcode)
    );
    return {
      bannerType: InlineBannerType.ERROR,
      text: error.message,
      ...((error.incorrectRegistryRelatedItemBarcodes?.length ?? 0) > 0 && {
        supportingInfo: (
          <span>{`Invalid items from a different registry: ${listToReadable(
            error.incorrectRegistryRelatedItemBarcodes!
          )}`}</span>
        ),
      }),
      ...(relatedItemBarcodesOfCorrectRegistry.length > 0 && {
        action: {
          actionType: InlineBannerActionType.CUSTOM,
          actionText: 'Show invalid items in search',
          actionOnClick: () =>
            navigateToURL(
              getValidationSearchUrl(relatedItemBarcodesOfCorrectRegistry, registryId, parentStorable.id)
            ),
        },
      }),
    };
  });

  const errorBanners = <InlineBannerStack messages={errorMessages} />;
  return errorMessages.length > 0 ? (
    <div className="u-moderate-spacing-top u-moderate-spacing-bottom">{errorBanners}</div>
  ) : null;
}

export default function QueryingParentStorableValidationErrors(
  props: ParentStorableValidationErrorProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    ParentStorableValidationErrorsQuery,
    ParentStorableValidationErrorsQueryVariables
  >(PARENT_STORABLE_VALIDATION_ERRORS_QUERY, {
    variables: {storableId: props.parentStorableId},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
  });

  if (queryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const parent = queryResult.data.storable;
  assert(!isInaccessible(parent), 'Expected parent storable to be accessible before loading errors');
  return <ParentStorableValidationErrors {...props} parentStorable={parent} />;
}
